// Authentication Management
import { auth, db } from './firebase.js';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    updateProfile,
    GoogleAuthProvider,
    signInWithPopup,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

// Current user state
let currentUser = null;
let authStateListeners = [];

/**
 * Register a listener for auth state changes
 */
export function onAuthChange(callback) {
    authStateListeners.push(callback);

    // Immediately call with current state
    if (currentUser !== null) {
        callback(currentUser);
    }

    // Return unsubscribe function
    return () => {
        authStateListeners = authStateListeners.filter(cb => cb !== callback);
    };
}

/**
 * Notify all listeners of auth state change
 */
function notifyAuthChange(user) {
    currentUser = user;
    authStateListeners.forEach(callback => callback(user));
}

/**
 * Create or update user document in Firestore
 */
async function syncUserDocument(user) {
    if (!user) return;

    try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);

        const userData = {
            displayName: user.displayName || user.email.split('@')[0] || 'Utilisateur Bible',
            email: user.email,
            photoURL: user.photoURL || null,
            language: 'fr', // Default language
            lastLoginAt: serverTimestamp(),
        };

        if (!userSnap.exists()) {
            // New user - create document
            userData.createdAt = serverTimestamp();
            console.log('✅ Creating new user document:', user.uid);
        } else {
            console.log('✅ Updating existing user document:', user.uid);
        }

        await setDoc(userRef, userData, { merge: true });
    } catch (error) {
        console.error('❌ Error syncing user document:', error);
        throw error;
    }
}

/**
 * Initialize auth state listener
 */
export function initAuth() {
    onAuthStateChanged(auth, async (user) => {
        if (user) {
            console.log('👤 User authenticated:', user.email);
            await syncUserDocument(user);
            notifyAuthChange(user);
        } else {
            console.log('👤 User signed out');
            notifyAuthChange(null);
        }
    });
}

/**
 * Sign in with email and password
 */
export async function signIn(email, password) {
    try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        console.log('✅ Signed in:', result.user.email);
        return result.user;
    } catch (error) {
        console.error('❌ Sign in error:', error);
        throw error;
    }
}

/**
 * Sign up with email and password
 */
export async function signUp(email, password, displayName) {
    try {
        const result = await createUserWithEmailAndPassword(auth, email, password);

        // Update display name
        if (displayName) {
            await updateProfile(result.user, { displayName });
            // Force a sync after profile update to ensure displayName is saved in Firestore
            await syncUserDocument(result.user);
        }

        console.log('✅ Signed up:', result.user.email);
        return result.user;
    } catch (error) {
        console.error('❌ Sign up error:', error);
        throw error;
    }
}

/**
 * Sign in with Google
 */
export async function signInWithGoogle() {
    try {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        console.log('✅ Signed in with Google:', result.user.email);
        return result.user;
    } catch (error) {
        console.error('❌ Google sign in error:', error);
        throw error;
    }
}

/**
 * Sign out current user
 */
export async function signOut() {
    try {
        await firebaseSignOut(auth);
        console.log('✅ Signed out successfully');
    } catch (error) {
        console.error('❌ Sign out error:', error);
        throw error;
    }
}

/**
 * Get current user
 */
export function getCurrentUser() {
    return currentUser;
}
